import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import Card from "../components/Card.jsx";
import MoneyComponent from "../elements/MoneyComponent.jsx";
import CircleLoader from "../components/CircleLoader.jsx";
import { useDispatch, useSelector } from "react-redux";
import { toggleShowClosedAccounts } from "../../redux/actions/plaid.js";
import { settingsTabChange } from "../../redux/actions/settings.js";
import { setSelectedMainTab } from "../../redux/actions/url.js";
import { IMPORT_RULES_TAB } from "./settings/ImportRules.jsx";
import { retrieveAccounts } from "../../redux/actions/accounts.js";
import AccountEditModal, { useAccountEditor } from "../components/accounts/AccountEditModal.jsx";
import AccountSummary from "../components/accounts/AccountSummary.jsx";
import Modal from "../components/Modal.jsx";
import MaterialIcon from "../elements/MaterialIcon.jsx";
import usePlaidAccounts from "../hooks/usePlaidAccounts.js";
import Button from "../components/Button.jsx";
import AccountRow from "./bank/AccountRow.jsx";
import InstitutionArrange from "./bank/InstitutionArrange.jsx";
import MonarchAccountMapper from "./bank/MonarchAccountMapper.jsx";
import { MonarchRefreshMenu, MonarchRefreshStatus, useMonarchConnection } from "./bank/MonarchBalanceRefresh.jsx";
import { countClosedAccounts, isInstitutionVisible } from "../../utilities/closedAccounts.js";
import { accountLimitOn, accountSections, accountTotals, closedAccountSections } from "../../utilities/accountSections.js";
import { twinHints } from "../../utilities/accountIdentity.js";

/** Balances are as of today in the current month, and as of the last day of any other. */
export function balanceDateFor(selectedDate, today = moment()) {
	if(!selectedDate) {
		return null;
	}
	if(selectedDate.isSame(today, 'month')) {
		return selectedDate.clone().date(today.date());
	}
	return selectedDate.clone().endOf('month');
}

/** The account with this id, and the institution holding it, from the page's list; null when it has gone. */
export function findAccount(institutions, accountId) {
	for(const institution of institutions || []) {
		for(const account of institution.accounts || []) {
			if(account.id === accountId) {
				return { account, institution };
			}
		}
	}
	return null;
}

/**
 * An account's summary, with its editor as a step inside the same popup, the way the food page's
 * popups step into an item: the box stays put, and its back arrow, Back and saving all come back to
 * the summary. `onOpenRules` is given the popup's `close`.
 */
function AccountSummaryPopup({ shown, origin, date, hint, onClose, onOpenRules, onOpenAccount }) {
	const [editing, setEditing] = useState(false);
	const editor = useAccountEditor({
		institution: shown.institution,
		account: shown.account,
		onLeave: () => setEditing(false),
		active: editing,
	});
	return (
		<Modal
			className='meal_idea_modal account_summary_modal'
			origin={origin}
			onClose={onClose}
			header={editing ? editor.header : null}
			onBack={editing ? editor.back : null}
		>
			{(close) => (editing
				? <div className='account_summary_editor' key={shown.account.id}>{editor.render(close)}</div>
				: <AccountSummary
					key={shown.account.id}
					account={shown.account}
					institution={shown.institution}
					date={date}
					hint={hint}
					onClose={close}
					onEdit={() => setEditing(true)}
					onOpenRules={() => onOpenRules(close)}
					onOpenAccount={onOpenAccount}
				/>
			)}
		</Modal>
	);
}

/** One section of the list: its heading, with what it adds up to when it has a subtotal, and a row per account. */
function AccountSection({ section, date, hints, onOpen }) {
	return (
		<section className={`account_section account_section_${section.key}`}>
			<header className='account_section_heading'>
				<span className='account_section_label'>{section.label}</span>
				{section.subtotal !== undefined &&
				<MoneyComponent amount={section.subtotal} showPositive={false} showNegative={false} />
				}
			</header>
			<div className='account_section_rows'>
				{section.rows.map((row) => (
					<AccountRow
						key={row.account.id}
						account={row.account}
						institution={row.institution}
						balance={row.balance}
						limit={accountLimitOn(row.account, date)}
						hint={hints.get(row.account.id)}
						onOpen={onOpen}
					/>
				))}
			</div>
		</section>
	);
}

/**
 * Every open account, a line each, under what kind of account it is, with what they add up to at the
 * top. Closed accounts, when shown, are sorted the same way into sections of their own under the toggle that shows them.
 * A row opens the account's summary, and the summary's Edit its editor, where everything about it is
 * changed. Arranging -- the order, and the institutions accounts are grouped under -- is a second
 * view, out of the way until asked for, and an editing mode, so there a row opens the editor itself.
 */
export default function BanksSummary() {
	const dispatch = useDispatch();
	const selectedDate = useSelector(state => state.dashboard.selectedDate);
	const retrievingPlaidAccounts = useSelector(state => state.plaid.retrievingPlaidAccounts);
	const plaidAccounts = usePlaidAccounts();
	const showClosedAccounts = useSelector(state => state.plaid.showClosedAccounts);
	const closedAccountCount = useMemo(() => countClosedAccounts(plaidAccounts), [plaidAccounts]);
	const [arranging, setArranging] = useState(false);
	/** The open form: an account, an institution, or { creating: 'any' } from Add, and the element it grew from. */
	const [editor, setEditor] = useState(null);
	/** The account whose summary is open, by id so it follows the list as it reloads, and the element it grew from. */
	const [summary, setSummary] = useState(null);
	/**
	 * What to do once the summary has finished closing: go to the import rules page. Leaving the page
	 * before then would leave the closing summary calling back into a page that has gone.
	 */
	const afterSummary = useRef(null);
	const monarchConnection = useMonarchConnection();

	useEffect(() => {
		dispatch(retrieveAccounts());
	}, []);

	const balanceDate = useMemo(() => balanceDateFor(selectedDate), [selectedDate]);
	const sections = useMemo(() => accountSections(plaidAccounts, balanceDate), [plaidAccounts, balanceDate]);
	const closedSections = useMemo(() => closedAccountSections(plaidAccounts, balanceDate), [plaidAccounts, balanceDate]);
	const totals = useMemo(() => accountTotals(plaidAccounts, balanceDate), [plaidAccounts, balanceDate]);
	/** Over every account, closed ones too, whichever are on show. */
	const hints = useMemo(() => twinHints((plaidAccounts || []).flatMap((institution) => institution.accounts || [])), [plaidAccounts]);
	const visibleInstitutions = useMemo(() => {
		return (plaidAccounts || []).filter((institution) => isInstitutionVisible(institution, showClosedAccounts));
	}, [plaidAccounts, showClosedAccounts]);
	const emptyInstitutions = useMemo(() => {
		return (plaidAccounts || []).filter((institution) => (institution.accounts || []).length === 0);
	}, [plaidAccounts]);

	const openAccount = (account, institution, origin) => setEditor({ account, institution, origin });
	const openSummary = (account, institution, origin) => setSummary({ accountId: account.id, origin });
	/**
	 * The last account shown stays shown while the popup closes on it: merging from the summary's
	 * editor deletes the account before the popup has closed.
	 */
	const lastShown = useRef(null);
	const found = summary ? findAccount(plaidAccounts, summary.accountId) : null;
	if(found) {
		lastShown.current = found;
	}
	const summaryShown = summary ? (found || lastShown.current) : null;
	const closeSummary = () => {
		const next = afterSummary.current;
		afterSummary.current = null;
		setSummary(null);
		if(next) {
			next();
		}
	};
	const openRulesFromSummary = (close) => {
		afterSummary.current = () => {
			dispatch(settingsTabChange(IMPORT_RULES_TAB));
			dispatch(setSelectedMainTab('settings'));
		};
		close();
	};
	/** Another account's summary in place of this one, closing into that account's own row. */
	const switchSummary = (account) => setSummary((current) => ({
		accountId: account.id,
		origin: document.querySelector(`.banks_summary .account_row[data-account-id="${account.id}"]`) || current.origin,
	}));
	const openInstitution = (institution, origin) => setEditor({ institution, origin });

	const showAccounts = plaidAccounts || !retrievingPlaidAccounts;
	return (
		<div className='banks_summary'>
			{showAccounts &&
			<div>
				{plaidAccounts &&
				<div className='account_totals'>
					<div className='account_total'>
						<span className='account_total_label'>Assets</span>
						<MoneyComponent amount={totals.assets} showPositive={false} showNegative={false} />
					</div>
					<div className='account_total'>
						<span className='account_total_label'>Debts</span>
						<MoneyComponent amount={totals.debts} showPositive={false} showNegative={false} />
					</div>
					<div className='account_total net_worth'>
						<span className='account_total_label'>Net worth</span>
						<MoneyComponent amount={totals.net} />
					</div>
				</div>
				}
				{!plaidAccounts &&
				<div>
					<Card>
						No Live Bank Account Data
					</Card>
				</div>
				}
				<div className='accounts_page_actions'>
					<Button
						flat
						small
						className='add_button'
						onClick={(event) => setEditor({ creating: 'any', origin: event.currentTarget })}
					>
						<MaterialIcon>add</MaterialIcon> Add
					</Button>
					<Button
						flat
						small
						className='arrange_accounts_button'
						onClick={() => setArranging((current) => !current)}
					>
						{arranging
							? <React.Fragment><MaterialIcon>check</MaterialIcon> Done</React.Fragment>
							: <React.Fragment><MaterialIcon>swap_vert</MaterialIcon> Arrange</React.Fragment>}
					</Button>
					<MonarchRefreshMenu connection={monarchConnection} />
				</div>
				<MonarchRefreshStatus connection={monarchConnection} />
				{!!monarchConnection && <MonarchAccountMapper />}
				{plaidAccounts && !arranging &&
				<div className='account_sections'>
					{sections.map((section) => (
						<AccountSection key={section.key} section={section} date={balanceDate} hints={hints} onOpen={openSummary} />
					))}
				</div>
				}
				{plaidAccounts && arranging &&
				<InstitutionArrange
					institutions={visibleInstitutions}
					emptyInstitutions={emptyInstitutions}
					showClosedAccounts={showClosedAccounts}
					date={balanceDate}
					hints={hints}
					onOpenAccount={openAccount}
					onOpenInstitution={openInstitution}
				/>
				}
				{closedAccountCount > 0 &&
				<div className='closed_accounts_toggle'>
					<Button
						flat
						small
						onClick={() => dispatch(toggleShowClosedAccounts())}
					>
						{showClosedAccounts
							? `Hide ${closedAccountCount === 1 ? 'closed account' : 'closed accounts'}`
							: `Show ${closedAccountCount} closed ${closedAccountCount === 1 ? 'account' : 'accounts'}`}
					</Button>
				</div>
				}
				{plaidAccounts && !arranging && showClosedAccounts && closedSections.length > 0 &&
				<div className='account_sections closed_account_sections'>
					<h3 className='closed_account_sections_heading'>Closed accounts</h3>
					{closedSections.map((section) => (
						<AccountSection key={section.key} section={section} date={balanceDate} hints={hints} onOpen={openSummary} />
					))}
				</div>
				}
			</div>
			}
			{summaryShown && ReactDOM.createPortal(
				<AccountSummaryPopup
					shown={summaryShown}
					origin={summary.origin}
					date={balanceDate}
					hint={hints.get(summaryShown.account.id)}
					onClose={closeSummary}
					onOpenRules={openRulesFromSummary}
					onOpenAccount={switchSummary}
				/>,
				document.body
			)}
			{editor && ReactDOM.createPortal(
				<AccountEditModal
					creating={editor.creating || null}
					institution={editor.institution || null}
					account={editor.account || null}
					origin={editor.origin}
					onClose={() => setEditor(null)}
				/>,
				document.body
			)}
			{retrievingPlaidAccounts &&
			<CircleLoader>
				{!!plaidAccounts ? 'Updating Accounts' : 'Retrieving Accounts'}
			</CircleLoader>
			}
		</div>
	);
}
